// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
/** The message the database raises when a write would change a sealed ballot box (VOTE-FREEZE). */
export var BALLOT_BOX_SEALED_ERROR = "ballot_box_sealed";
/** The texts of an error: its message, and what Hasura or Apollo carry along. */
var errorTexts = function (error, seen) {
    if (seen === void 0) { seen = new Set(); }
    if (error === null || error === undefined || seen.has(error))
        return [];
    if (typeof error === "string")
        return [error];
    if (typeof error !== "object")
        return [];
    seen.add(error);
    var texts = [];
    for (var _i = 0, _a = Object.values(error); _i < _a.length; _i++) {
        var value = _a[_i];
        texts.push.apply(texts, errorTexts(value, seen));
    }
    if (error instanceof Error)
        texts.push(error.message);
    return texts;
};
/**
 * Whether the database refused a write because a ballot box is sealed, e.g.
 * deleting an election with seals. The refusal is matched anywhere in the
 * error: Hasura may put it in the message or in the extensions.
 */
export var isBallotBoxSealedError = function (error) {
    return errorTexts(error).some(function (text) { return text.includes(BALLOT_BOX_SEALED_ERROR); });
};
/**
 * The translation key of a known server text about sealing (VOTE-FREEZE),
 * so the portal shows it in the admin's language; undefined for any other
 * text, which is then shown as it is.
 */
var KNOWN_TEXTS = [
    // why a seal failed (`failure_reason`)
    [/^a ballot does not match its Ballot ID/i, "dashboard.ballotBoxes.failure.ballotIdMismatch"],
    [/has no content or no Ballot ID/i, "dashboard.ballotBoxes.failure.missingContent"],
    [/can't be read:/i, "dashboard.ballotBoxes.failure.unreadable"],
    [/is still in progress$/i, "dashboard.ballotBoxes.failure.inProgress"],
    [/already on the bulletin board/i, "dashboard.ballotBoxes.failure.alreadyOnBoard"],
    [/has no bulletin board/i, "dashboard.ballotBoxes.failure.noBoard"],
    [/has the unknown voting channel/i, "dashboard.ballotBoxes.failure.unknownChannel"],
    [/have no cast-vote record on the bulletin board/i, "dashboard.ballotBoxes.failure.unrecorded"],
    // refusals of actions
    [/^Voting can't start again/i, "publish.sealRefusals.startAgain"],
    [
        /^This election event has sealed ballot boxes and cannot be deleted/i,
        "sideMenu.menuActions.messages.notification.error.deleteSealedEvent",
    ],
    [
        /can only change before voting opens/i,
        "electionEventScreen.field.ballotBoxSealPolicy.refused",
    ],
    // the settings guard's refusals (1791000001700, 1791000001800)
    [
        /(contest_encryption_policy|delegated_voting_policy|weighted_voting_policy|ballot_box_seal_record_policy) of election event .* can't change after voting has opened/i,
        "electionEventScreen.field.ballotBoxSealPolicy.settingRefused",
    ],
    [
        /The bulletin board of election event .* can't change after voting has opened/i,
        "electionEventScreen.field.ballotBoxSealPolicy.boardRefused",
    ],
    [/ballot_box_seal_closed_is_final/, "publish.sealRefusals.closedIsFinal"],
];
export var sealTextKey = function (text) { var _a; return text ? (_a = KNOWN_TEXTS.find(function (_a) {
    var pattern = _a[0];
    return pattern.test(text);
})) === null || _a === void 0 ? void 0 : _a[1] : undefined; };
/** A server text about sealing in the admin's language, or as it is when it isn't a known one. */
export var sealText = function (t, text) {
    var key = sealTextKey(text);
    return key ? t(key) : (text !== null && text !== void 0 ? text : "");
};
/** A known seal refusal found anywhere in an error, in the admin's language; undefined otherwise. */
export var sealErrorText = function (t, error) {
    var key = errorTexts(error)
        .map(function (text) { return sealTextKey(text); })
        .find(Boolean);
    return key ? t(key) : undefined;
};
